// Query keys and fetchers for every call the portal makes. Paths are the
// contracts' (httpapi/v1 broker_portal.go const block and the phase
// 02/07a/07b staff routes in broker-service handlers_staff.go,
// handlers_revenue.go, handlers_statement.go, handlers_clearing.go).
//
// Every node-scoped key starts ["broker", <node id>, …] so switching the
// selected node can never serve one node's figures under another's name,
// and a session teardown is one queryClient.clear().
//
// broker_account_id NEVER travels in a query or a body: api-gateway
// strips it on /api/* (SM-693). One account's reads use the path.
import { api, auditRefs, qs, request, seg } from "./api.ts";
import { EXPOSURE_CURRENCY } from "./currency.ts";
import type {
  AccountScope,
  BrokerCurrencyImageResponse,
  BrokerAccountEventListResponse,
  BrokerAccountItem,
  BrokerAccountListResponse,
  BrokerAccountLoginListResponse,
  BrokerAccountTransitionRequest,
  BrokerAuditListResponse,
  BrokerClearingBalance,
  BrokerItem,
  BrokerListResponse,
  BrokerOverviewResponse,
  ChangeStaffRoleRequest,
  CreateBrokerRequest,
  CreateFeePlanRequest,
  CreateRevenueSharePlanRequest,
  CurrencyInfo,
  EarningListResponse,
  ExposureView,
  FeeAssignmentItem,
  FeeAssignmentListResponse,
  FeePlanItem,
  FeePlanListResponse,
  FeePlanVersionItem,
  FeePreviewRequest,
  FeePreviewResponse,
  FundingPoolListResponse,
  FundingTransferItem,
  FundingTransferListResponse,
  InviteStaffRequest,
  OrderClientLogListResponse,
  OrderListResponse,
  PayoutBatchListResponse,
  PoolTransferRequest,
  PositionListResponse,
  PublishFeeAssignmentRequest,
  PublishFeePlanVersionRequest,
  PublishRevenueShareAssignmentRequest,
  PublishRevenueShareVersionRequest,
  RedemptionPreviewRequest,
  RedemptionPreviewResponse,
  RetireFeePlanRequest,
  RetireRevenueSharePlanRequest,
  RevenueShareAssignmentItem,
  RevenueShareAssignmentListResponse,
  RevenueSharePlanDetail,
  RevenueSharePlanItem,
  RevenueSharePlanListResponse,
  RevenueSharePlanVersionItem,
  RevenueBalancesResponse,
  RevenuePeriodsResponse,
  RevenueStatement,
  SettlementNoteListResponse,
  PaymentRequestListResponse,
  SettlementPositionResponse,
  SettlementTradeListResponse,
  StaffFeeScheduleResponse,
  StaffMembershipItem,
  StaffListParams,
  StaffMembershipListResponse,
  StaffPathResponse,
  StaffReconciliationResponse,
  StaffTransitionRequest,
  StatementScope,
} from "./types.ts";

const B = "/api/broker";

type Params = Record<string, string | number | boolean | undefined>;

export const keys = {
  me: () => ["auth", "me"] as const,
  staffMe: () => ["broker-me"] as const,
  currencies: () => ["currencies"] as const,
  node: (id: string) => ["broker", id, "node"] as const,
  path: (id: string) => ["broker", id, "path"] as const,
  children: (id: string, p: Params) => ["broker", id, "children", p] as const,
  descendants: (id: string, p: Params) => ["broker", id, "descendants", p] as const,
  audit: (id: string, p: Params) => ["broker", id, "audit", p] as const,
  /** SM-978: the node's GOLD image (the topbar and every figure draw it). */
  currencyImage: (id: string) => ["broker", id, "currency-image"] as const,
  staff: (id: string, p: Params) => ["broker", id, "staff", p] as const,
  accounts: (id: string, p: Params) => ["broker", id, "accounts", p] as const,
  account: (id: string, aid: string) => ["broker", id, "account", aid] as const,
  accountEvents: (id: string, aid: string, p: Params) => ["broker", id, "account", aid, "events", p] as const,
  accountPositions: (id: string, aid: string, p: Params) => ["broker", id, "account", aid, "positions", p] as const,
  accountOrders: (id: string, aid: string, p: Params) => ["broker", id, "account", aid, "orders", p] as const,
  accountOrderHistory: (id: string, aid: string, p: Params) => ["broker", id, "account", aid, "order-history", p] as const,
  accountTrades: (id: string, aid: string, p: Params) => ["broker", id, "account", aid, "trades", p] as const,
  accountLoginLog: (id: string, aid: string, p: Params) => ["broker", id, "account", aid, "login-log", p] as const,
  accountOrderLog: (id: string, aid: string, p: Params) => ["broker", id, "account", aid, "order-log", p] as const,
  positions: (id: string, p: Params) => ["broker", id, "positions", p] as const,
  orders: (id: string, p: Params) => ["broker", id, "orders", p] as const,
  orderHistory: (id: string, p: Params) => ["broker", id, "order-history", p] as const,
  trades: (id: string, p: Params) => ["broker", id, "trades", p] as const,
  exposure: (id: string) => ["broker", id, "exposure"] as const,
  pools: (id: string, currency: string) => ["broker", id, "pools", currency] as const,
  transfers: (id: string, p: Params) => ["broker", id, "transfers", p] as const,
  clearing: (id: string, currency: string) => ["broker", id, "clearing", currency] as const,
  settlementPosition: (id: string, p: Params) => ["broker", id, "settlement-position", p] as const,
  settlementNotes: (id: string, p: Params) => ["broker", id, "settlement-notes", p] as const,
  paymentRequests: (id: string, p: Params) => ["broker", id, "payment-requests", p] as const,
  feeSchedule: (id: string, currency: string) => ["broker", id, "fee-schedule", currency] as const,
  // authoring (FEE_MANAGE): id is always the ROOT node
  feePlans: (root: string, p: Params) => ["broker", root, "fee-plans", p] as const,
  feePlan: (root: string, planId: string) => ["broker", root, "fee-plan", planId] as const,
  feeAssignments: (root: string, p: Params) => ["broker", root, "fee-assignments", p] as const,
  sharePlans: (root: string, p: Params) => ["broker", root, "share-plans", p] as const,
  sharePlan: (root: string, planId: string) => ["broker", root, "share-plan", planId] as const,
  shareAssignments: (root: string, p: Params) => ["broker", root, "share-assignments", p] as const,
  revenueBalances: (id: string) => ["broker", id, "revenue-balances"] as const,
  revenuePeriods: (id: string, currency: string) => ["broker", id, "revenue-periods", currency] as const,
  earnings: (id: string, p: Params) => ["broker", id, "earnings", p] as const,
  payouts: (id: string, p: Params) => ["broker", id, "payouts", p] as const,
  statement: (id: string, p: Params) => ["broker", id, "statement", p] as const,
  reconciliation: (id: string, currency: string) => ["broker", id, "reconciliation", currency] as const,
  overview: (id: string, currency: string) => ["broker", id, "overview", currency] as const,
};

/** A mutation's answer plus the audit events it wrote (plan D65). */
export interface Mutated<T> {
  data: T;
  auditIds: string[];
  status: number;
}

async function mutate<T>(path: string, body: unknown, idempotencyKey?: string): Promise<Mutated<T>> {
  const res = await request<T>(path, { method: "POST", body, idempotencyKey });
  return { data: res.data, auditIds: auditRefs(res.headers), status: res.status };
}

// --- presentation registry ---------------------------------------------------------

/** GET /api/currencies. Presentation only; a failure degrades to the seed. */
export async function fetchCurrencies(): Promise<CurrencyInfo[]> {
  const r = await api<{ currencies?: CurrencyInfo[] }>("/api/currencies");
  return r?.currencies ?? [];
}

// --- the node's GOLD currency image (SM-978) ---------------------------------------

/** GET /api/broker/{id}/currency-image (VIEW). */
export const fetchCurrencyImage = (id: string) =>
  api<BrokerCurrencyImageResponse>(`${B}/${seg(id)}/currency-image`);

/** POST /api/broker/{id}/currency-image (BRANDING_MANAGE): multipart field "file". */
export async function uploadCurrencyImage(id: string, file: Blob, name = "image"): Promise<Mutated<BrokerCurrencyImageResponse>> {
  const form = new FormData();
  form.append("file", file, name);
  const res = await request<BrokerCurrencyImageResponse>(`${B}/${seg(id)}/currency-image`, { method: "POST", form });
  return { data: res.data, auditIds: auditRefs(res.headers), status: res.status };
}

/** DELETE /api/broker/{id}/currency-image (BRANDING_MANAGE): idempotent. */
export async function removeCurrencyImage(id: string): Promise<Mutated<BrokerCurrencyImageResponse>> {
  const res = await request<BrokerCurrencyImageResponse>(`${B}/${seg(id)}/currency-image`, { method: "DELETE" });
  return { data: res.data, auditIds: auditRefs(res.headers), status: res.status };
}

// --- memberships -------------------------------------------------------------------

/** POST /api/broker/me/memberships/{id}/activate — accept an invitation. */
export const activateMembership = (membershipId: string) =>
  mutate<StaffMembershipItem>(`${B}/me/memberships/${seg(membershipId)}/activate`, undefined);

/** POST /api/broker/me/memberships/{id}/leave — decline / leave, with a reason. */
export const leaveMembership = (membershipId: string, body: StaffTransitionRequest) =>
  mutate<StaffMembershipItem>(`${B}/me/memberships/${seg(membershipId)}/leave`, body);

// --- organization + hierarchy (VIEW) ----------------------------------------------

export const fetchNode = (id: string) => api<BrokerItem>(`${B}/${seg(id)}`);

export const fetchPath = (id: string) => api<StaffPathResponse>(`${B}/${seg(id)}/path`);

export const fetchChildren = (id: string, p: { q?: string; status?: string; offset?: number; limit?: number }) =>
  api<BrokerListResponse>(`${B}/${seg(id)}/children${qs(p)}`);

export const fetchDescendants = (
  id: string,
  p: { q?: string; include_self?: boolean; offset?: number; limit?: number },
) => api<BrokerListResponse>(`${B}/${seg(id)}/descendants${qs(p)}`);

export const fetchAudit = (id: string, p: { offset?: number; limit?: number }) =>
  api<BrokerAuditListResponse>(`${B}/${seg(id)}/audit${qs(p)}`);

/** POST /{id}/children (CHILD_CREATE): lands a DRAFT awaiting exchange approval. */
/** POST /{id}/children with the dialog's Idempotency-Key (SM-964): a retry answers the first sub-broker. */
export const createChild = (id: string, body: CreateBrokerRequest, idempotencyKey?: string) =>
  mutate<BrokerItem>(`${B}/${seg(id)}/children`, body, idempotencyKey);

// --- staff (VIEW; STAFF_MANAGE on the member's node and its descendants, SM-852) ---

export const fetchStaff = (id: string, p: StaffListParams) => api<StaffMembershipListResponse>(`${B}/${seg(id)}/staff${qs(p)}`);

export const inviteStaff = (id: string, body: InviteStaffRequest) =>
  mutate<StaffMembershipItem>(`${B}/${seg(id)}/staff`, body);

export type StaffCommand = "suspend" | "resume" | "remove";

export const staffCommand = (id: string, membershipId: string, command: StaffCommand, body: StaffTransitionRequest) =>
  mutate<StaffMembershipItem>(`${B}/${seg(id)}/staff/${seg(membershipId)}/${command}`, body);

export const changeStaffRole = (id: string, membershipId: string, body: ChangeStaffRoleRequest) =>
  mutate<StaffMembershipItem>(`${B}/${seg(id)}/staff/${seg(membershipId)}/role`, body);

/** A fresh set-password link for an invitee with no password yet (SM-968); the old link stops working. */
export const resendInvite = (id: string, membershipId: string, body: StaffTransitionRequest) =>
  mutate<StaffMembershipItem>(`${B}/${seg(id)}/staff/${seg(membershipId)}/resend-invite`, body);

// --- users (VIEW; ACCOUNT_PAUSE on directly serviced accounts) -------------------

export interface AccountListParams {
  scope?: AccountScope;
  status?: string;
  balance_mode?: string;
  q?: string;
  offset?: number;
  limit?: number;
}

export const fetchAccounts = (id: string, p: AccountListParams) =>
  api<BrokerAccountListResponse>(`${B}/${seg(id)}/accounts${qs({ ...p })}`);

export const fetchAccount = (id: string, accountId: string) =>
  api<BrokerAccountItem>(`${B}/${seg(id)}/accounts/${seg(accountId)}`);

export const fetchAccountEvents = (id: string, accountId: string, p: { offset?: number; limit?: number }) =>
  api<BrokerAccountEventListResponse>(`${B}/${seg(id)}/accounts/${seg(accountId)}/events${qs(p)}`);

/** One account's positions — the account in the PATH (SM-693). */
export const fetchAccountPositions = (
  id: string,
  accountId: string,
  p: { currency: string; status?: string; offset?: number; limit?: number },
) => api<PositionListResponse>(`${B}/${seg(id)}/accounts/${seg(accountId)}/positions${qs(p)}`);

/** One account's logins (SM-822) — the account in the PATH. */
export const fetchAccountLoginLog = (id: string, accountId: string, p: { offset?: number; limit?: number }) =>
  api<BrokerAccountLoginListResponse>(`${B}/${seg(id)}/accounts/${seg(accountId)}/login-log${qs(p)}`);

/**
 * Where one account's orders were placed from (SM-822) — the account in the
 * PATH. Keyset-paged: no cursor is the first page, then the previous page's
 * next_cursor, verbatim.
 */
export const fetchAccountOrderLog = (id: string, accountId: string, p: { cursor?: string; limit?: number }) =>
  api<OrderClientLogListResponse>(`${B}/${seg(id)}/accounts/${seg(accountId)}/order-log${qs(p)}`);

export type AccountCommand = "suspend" | "resume";

export const accountCommand = (id: string, accountId: string, command: AccountCommand, body: BrokerAccountTransitionRequest) =>
  mutate<BrokerAccountItem>(`${B}/${seg(id)}/accounts/${seg(accountId)}/${command}`, body);

// --- positions + risk (VIEW) ---------------------------------------------------------

export const fetchPositions = (
  id: string,
  p: { scope?: AccountScope; currency: string; status?: string; offset?: number; limit?: number },
) => api<PositionListResponse>(`${B}/${seg(id)}/positions${qs(p)}`);

// Always GOLD, never the shell's currency: broker-service refuses any
// other exposure currency with a 400 (SM-768 B7, SM-848).
export const fetchExposure = (id: string) =>
  api<ExposureView>(`${B}/${seg(id)}/exposure${qs({ currency: EXPOSURE_CURRENCY })}`);

// --- trading (VIEW, read-only; SM-823) ------------------------------------------------
//
// Working orders, finished orders (one row per order) and fills, from
// order-service and settlement-service through broker-service's staff
// routes. Every read takes ?currency= and pages with offset/limit (the
// server defaults to 10, at most 200). One account's reads put the account
// in the PATH and have no default window; the node-wide history and trades
// default to the last 30 days and refuse more than 92, which is why the
// Trading pages always send both ends.

export interface TradingPage {
  currency: string;
  offset?: number;
  limit?: number;
}

/** [from_unix_ms, to_unix_ms): history and trades only. */
export interface TradingWindow {
  from_unix_ms?: number;
  to_unix_ms?: number;
}

export type OrderHistoryParams = TradingPage & TradingWindow & { status?: string };

/** One account's working orders (SM-828) — the account in the PATH. */
export const fetchAccountOrders = (id: string, accountId: string, p: TradingPage) =>
  api<OrderListResponse>(`${B}/${seg(id)}/accounts/${seg(accountId)}/orders${qs({ ...p })}`);

/** One account's finished orders, one row each (SM-828) — the account in the PATH. */
export const fetchAccountOrderHistory = (id: string, accountId: string, p: OrderHistoryParams) =>
  api<OrderListResponse>(`${B}/${seg(id)}/accounts/${seg(accountId)}/orders/history${qs({ ...p })}`);

/** One account's fills, one row per side (SM-828) — the account in the PATH. */
export const fetchAccountTrades = (id: string, accountId: string, p: TradingPage & TradingWindow) =>
  api<SettlementTradeListResponse>(`${B}/${seg(id)}/accounts/${seg(accountId)}/trades${qs({ ...p })}`);

/** The node's (or subtree's) working orders. */
export const fetchOrders = (id: string, p: TradingPage & { scope?: AccountScope }) =>
  api<OrderListResponse>(`${B}/${seg(id)}/orders${qs({ ...p })}`);

/** The node's (or subtree's) finished orders. */
export const fetchOrderHistory = (id: string, p: OrderHistoryParams & { scope?: AccountScope }) =>
  api<OrderListResponse>(`${B}/${seg(id)}/orders/history${qs({ ...p })}`);

/** The node's (or subtree's) fills. */
export const fetchTrades = (id: string, p: TradingPage & TradingWindow & { scope?: AccountScope }) =>
  api<SettlementTradeListResponse>(`${B}/${seg(id)}/trades${qs({ ...p })}`);

// --- funding (FINANCE_VIEW; FUNDING_REQUEST for the transfer) --------------------

export const fetchPools = (id: string, currency: string) =>
  api<FundingPoolListResponse>(`${B}/${seg(id)}/funding/pools${qs({ currency })}`);

export const fetchTransfers = (id: string, p: { currency: string; kind?: string; cursor?: string; limit?: number }) =>
  api<FundingTransferListResponse>(`${B}/${seg(id)}/funding/transfers${qs(p)}`);

export const fetchClearing = (id: string, currency: string) =>
  api<BrokerClearingBalance>(`${B}/${seg(id)}/clearing${qs({ currency })}`);

// --- settlement position (FINANCE_VIEW, read only; SM-797) ---------------------------
//
// One engine in broker-service; backoffice reads the same figures. There
// is deliberately no note write here: recording a settlement is an
// exchange-side action.

export interface SettlementParams {
  currency: string;
  from_unix_ms: number;
  to_unix_ms: number;
  scope: "node" | "subtree";
}

export const fetchSettlementPosition = (id: string, p: SettlementParams) =>
  api<SettlementPositionResponse>(`${B}/${seg(id)}/settlement-position${qs({ ...p })}`);

export const fetchSettlementNotes = (id: string, p: { currency: string; scope: "node" | "subtree"; limit: number; offset: number }) =>
  api<SettlementNoteListResponse>(`${B}/${seg(id)}/settlement-notes${qs({ ...p })}`);

/**
 * The payment requests the exchange sent this node (scope=node) or this
 * node and its descendants (scope=subtree), newest first (SM-883). Read
 * only: recording and cancelling a request is an exchange-side action.
 * status: REQUESTED for the open ones, omitted for all.
 */
export const fetchPaymentRequests = (id: string, p: { scope: "node" | "subtree"; status?: string; limit: number; offset: number }) =>
  api<PaymentRequestListResponse>(`${B}/${seg(id)}/payment-requests${qs({ ...p })}`);

/** POST /{id}/funding/pool-transfers with the dialog's Idempotency-Key. */
export const poolTransfer = (id: string, body: PoolTransferRequest, idempotencyKey: string) =>
  mutate<FundingTransferItem>(`${B}/${seg(id)}/funding/pool-transfers`, body, idempotencyKey);

// --- fees (VIEW) ---------------------------------------------------------------------

export const fetchFeeSchedule = (id: string, currency: string) =>
  api<StaffFeeScheduleResponse>(`${B}/${seg(id)}/fees/schedule${qs({ currency })}`);

export const feePreview = (id: string, body: FeePreviewRequest) =>
  api<FeePreviewResponse>(`${B}/${seg(id)}/fees/preview`, { method: "POST", body });

export const redemptionPreview = (id: string, body: RedemptionPreviewRequest) =>
  api<RedemptionPreviewResponse>(`${B}/${seg(id)}/fees/redemption-preview`, { method: "POST", body });

/**
 * Every node's fee-schedule query, whichever node and currency: a version,
 * a retirement or an assignment can change what any node of the tree
 * resolves, so a confirmed authoring write refreshes them all.
 */
export function isFeeScheduleKey(key: readonly unknown[]): boolean {
  return key[0] === "broker" && key[2] === "fee-schedule";
}

// --- fee-plan authoring (FEE_MANAGE on the ROOT node; SM-844) -------------------------
//
// The root's OWNER and FINANCE, through the same broker-service methods the
// exchange uses (bodies and answers are the operator surface's). The node
// in the path is always the ROOT, never a body field. No Idempotency-Key:
// the dialogs lock after an unknown outcome instead (lib/outcome.ts).

export const fetchFeePlans = (root: string, p: { offset?: number; limit?: number }) =>
  api<FeePlanListResponse>(`${B}/${seg(root)}/fee-plans${qs(p)}`);

/** One plan with every version, all currencies. */
export const fetchFeePlan = (root: string, planId: string) =>
  api<FeePlanItem>(`${B}/${seg(root)}/fee-plans/${seg(planId)}`);

export const createFeePlan = (root: string, body: CreateFeePlanRequest) => mutate<FeePlanItem>(`${B}/${seg(root)}/fee-plans`, body);

export const publishFeePlanVersion = (root: string, planId: string, body: PublishFeePlanVersionRequest) =>
  mutate<FeePlanVersionItem>(`${B}/${seg(root)}/fee-plans/${seg(planId)}/versions`, body);

export const retireFeePlan = (root: string, planId: string, body: RetireFeePlanRequest) =>
  mutate<FeePlanItem>(`${B}/${seg(root)}/fee-plans/${seg(planId)}/retire`, body);

/**
 * The root's assignment HISTORY, newest effective first (SM-845). Never
 * read a node's current plan from it — the fee schedule answers that.
 */
export const fetchFeeAssignments = (root: string, p: { offset?: number; limit?: number }) =>
  api<FeeAssignmentListResponse>(`${B}/${seg(root)}/fee-assignments${qs(p)}`);

export const publishFeeAssignment = (root: string, body: PublishFeeAssignmentRequest) =>
  mutate<FeeAssignmentItem>(`${B}/${seg(root)}/fee-assignments`, body);

// --- revenue-share authoring (FEE_MANAGE on the ROOT node; SM-846) -------------------

export const fetchSharePlans = (root: string, p: { offset?: number; limit?: number }) =>
  api<RevenueSharePlanListResponse>(`${B}/${seg(root)}/revenue-share-plans${qs(p)}`);

/** One plan with every version, newest first. */
export const fetchSharePlan = (root: string, planId: string) =>
  api<RevenueSharePlanDetail>(`${B}/${seg(root)}/revenue-share-plans/${seg(planId)}`);

export const createSharePlan = (root: string, body: CreateRevenueSharePlanRequest) =>
  mutate<RevenueSharePlanItem>(`${B}/${seg(root)}/revenue-share-plans`, body);

export const publishShareVersion = (root: string, planId: string, body: PublishRevenueShareVersionRequest) =>
  mutate<RevenueSharePlanVersionItem>(`${B}/${seg(root)}/revenue-share-plans/${seg(planId)}/versions`, body);

export const retireSharePlan = (root: string, planId: string, body: RetireRevenueSharePlanRequest) =>
  mutate<RevenueSharePlanItem>(`${B}/${seg(root)}/revenue-share-plans/${seg(planId)}/retire`, body);

/** The root's share assignment HISTORY, newest effective first. */
export const fetchShareAssignments = (root: string, p: { offset?: number; limit?: number }) =>
  api<RevenueShareAssignmentListResponse>(`${B}/${seg(root)}/revenue-share-assignments${qs(p)}`);

export const publishShareAssignment = (root: string, body: PublishRevenueShareAssignmentRequest) =>
  mutate<RevenueShareAssignmentItem>(`${B}/${seg(root)}/revenue-share-assignments`, body);

// --- revenue, payouts, statements, reconciliation (FINANCE_VIEW) -----------------

export const fetchRevenueBalances = (id: string) => api<RevenueBalancesResponse>(`${B}/${seg(id)}/revenue/balances`);

export const fetchRevenuePeriods = (id: string, currency: string) =>
  api<RevenuePeriodsResponse>(`${B}/${seg(id)}/revenue/periods${qs({ currency })}`);

export const fetchEarnings = (
  id: string,
  p: {
    currency: string;
    state?: string;
    role?: string;
    from_unix_ms?: number;
    to_unix_ms?: number;
    offset?: number;
    limit?: number;
  },
) => api<EarningListResponse>(`${B}/${seg(id)}/revenue/earnings${qs(p)}`);

export const fetchPayouts = (id: string, p: { currency: string; state?: string; offset?: number; limit?: number }) =>
  api<PayoutBatchListResponse>(`${B}/${seg(id)}/payouts${qs(p)}`);

export interface StatementParams {
  currency: string;
  from_unix_ms: number;
  to_unix_ms: number;
  scope: StatementScope;
  offset?: number;
  limit?: number;
}

export const fetchStatement = (id: string, p: StatementParams) =>
  api<RevenueStatement>(`${B}/${seg(id)}/revenue/statement${qs({ ...p })}`);

/** The CSV export link (a plain GET download on the session cookie). */
export function statementCsvUrl(id: string, p: Omit<StatementParams, "offset" | "limit">): string {
  return `${B}/${seg(id)}/revenue/statement.csv${qs({ ...p })}`;
}

/** One currency's page: the server keeps that currency's rows and judges each run's status over them (SM-939). */
export const fetchReconciliation = (id: string, currency: string) =>
  api<StaffReconciliationResponse>(`${B}/${seg(id)}/reconciliation${qs({ currency })}`);

// --- overview (VIEW; money panels FINANCE_VIEW) -----------------------------------

export const fetchOverview = (id: string, currency: string) =>
  api<BrokerOverviewResponse>(`${B}/${seg(id)}/overview${qs({ currency })}`);
